import type { Sale, CustomerPayment, SupplierPayment, Customer, CardProvider, CashMovement, PrepaidMove } from './mock-data'

/**
 * What actually reached the drawer — the cash formula, in ONE place.
 *
 * WHY THIS FILE EXISTS. The formula already lived in two: `close_shift()` in
 * migration 010 and the live preview in `close-shift-dialog.tsx`. They agreed,
 * which is lucky rather than guaranteed, and the accounting page then grew a
 * THIRD idea of what "cash" means — `sum(sales where payment_method = 'cash')`
 * under a label reading „სალაროში შემოსული".
 *
 * That third one was wrong in a way a shopkeeper feels before he can name it.
 * Sell ₾20.60 on ნისია, collect it in cash the same evening, and the number
 * under „received into the till" does not move — not by the sale, not by the
 * payment. The money is in the drawer; the screen says it never arrived. The
 * owner found it on 2026-09-09 by trusting his gut over the dashboard.
 *
 * A credit sale keeps `payment_method = 'credit'` for ever. It is a fact about
 * how the sale was agreed, not about where the money is now. The repayment
 * lives in `customer_payments`, a different table entirely, which that sum
 * could not see.
 *
 * So: this module owns the definition, `close-shift-dialog.tsx` calls it
 * instead of keeping its own copy, and `cash.test.ts` pins it to the SQL. If
 * `close_shift()` in Postgres ever changes, these tests fail — that is their
 * job.
 */

export type CashTerms = {
  /** Sales rung up as cash. */
  cashSales: number
  /** The CASH half of what was paid at the till on a ნისია sale. Drawer money
   *  that the sale's own payment_method hides under „ნისია".
   *
   *  Before 103 this was `sales.paid` and the rest of the app simply ASSUMED
   *  the deposit had been cash. It now reads `paid_cash`, so a deposit taken
   *  on a card is counted where it belongs: nowhere near the drawer. */
  creditDownPayments: number
  /** A customer paying down an old debt, later, in cash. */
  debtRepayments: number
  /** Refunded out of the drawer. */
  cashReturns: number
  /** Paid to a supplier out of the drawer. */
  supplierCash: number
  /** 107 — money put INTO the drawer during the shift (change brought in, or
   *  something named). Not takings, so it is not in `cashIn`. */
  drawerIn: number
  /** 107 — money taken OUT with a reason: the owner took it, an expense, the
   *  bank. Not a refund and not a supplier, so it is its own term. */
  drawerOut: number
  /** 168 — the tetri the cashier kept when rounding the change down (a sale's
   *  `change_kept` above 0). In the drawer, not takings: not in `cashIn`. */
  changeKeptIn: number
  /** 168 — the tetri the cashier gave when rounding up, or forgave a customer
   *  who was short (`change_kept` below 0), as a positive amount. */
  changeKeptOut: number
  /** 176 — cash taken for a gift card or a deposit during the shift. In the drawer,
   *  not takings (the sale happens when it is spent): not in `cashIn`. */
  prepaidCashIn: number
  /** 176 — cash handed back from a gift card or a deposit (a refund), as a positive amount. */
  prepaidCashOut: number
  /** Everything that came IN. The honest answer to „how much cash did I take?" */
  cashIn: number
  /** What should be left: `cashIn` less what was paid back out, plus and minus
   *  what was put in and taken out. Add an opening float for a shift's
   *  expected drawer. */
  netCash: number
}

/**
 * The rows must already be scoped by the caller — one shift, or one period and
 * branch. This function does not filter; it only adds up.
 *
 * ⚠️ SINCE MIGRATION 060 both payment tables DO carry a `branch_id`, so a
 * branch-filtered caller can scope them — but only rows written after 060 have
 * one. Older rows, and any payment taken with no shift to name a branch, are
 * deliberately NULL rather than guessed (060 refuses the coin toss; 062 made
 * the RPCs refuse it too). A branch view therefore drops them, and the caller
 * must SAY how much it dropped — in lari, on screen — instead of quietly
 * showing a smaller business. This comment described the pre-060 world and was
 * left behind by that migration; in this codebase the comments are the spec,
 * so a stale one sends the next reader to re-add a caveat that is now wrong.
 */
export function cashTerms({
  sales,
  customerPayments,
  supplierPayments = [],
  cashMovements = [],
  prepaidMoves = [],
}: {
  /** 1.27 — a sale or a SaleBucket (lib/sale-buckets.ts): only these are read.
   *  168: a bucket carries no `change_kept` and counts 0 — it feeds `cashIn`
   *  only, which the rounding is not part of. */
  sales: readonly (Pick<Sale, 'type' | 'payment_method' | 'paid_cash'> & { change_kept?: number | null })[]
  customerPayments: CustomerPayment[]
  supplierPayments?: SupplierPayment[]
  cashMovements?: CashMovement[]
  /** 176 — the shift's (or period's) prepaid moves; only the cash ones reach the drawer. */
  prepaidMoves?: readonly PrepaidMove[]
}): CashTerms {
  let cashSales = 0
  let creditDownPayments = 0
  let cashReturns = 0
  let changeKeptIn = 0
  let changeKeptOut = 0

  // 103 — every term below reads `paid_cash`, term for term the same as
  // `close_shift()`. Nothing here looks at `payment_method` to decide how much
  // money moved; the method is only used to split the figure into the two the
  // Z-report prints separately.
  for (const s of sales) {
    if (s.type === 'return') {
      // Only a refund handed back in cash leaves the drawer. A card refund goes
      // back down the card rail and must not appear here.
      cashReturns += s.paid_cash ?? 0
      continue
    }
    if (s.payment_method === 'credit') creditDownPayments += s.paid_cash ?? 0
    else cashSales += s.paid_cash ?? 0
    // 168 — as close_shift(): a sale's rounding, kept and given apart
    const kept = s.change_kept ?? 0
    if (kept > 0) changeKeptIn += kept
    else if (kept < 0) changeKeptOut -= kept
  }

  const debtRepayments = customerPayments
    .filter(p => p.method === 'cash')
    .reduce((a, p) => a + p.amount, 0)

  const supplierCash = supplierPayments
    .filter(p => p.method === 'cash')
    .reduce((a, p) => a + p.amount, 0)

  let drawerIn = 0
  let drawerOut = 0
  for (const m of cashMovements) {
    if (m.direction === 'in') drawerIn += m.amount
    else drawerOut += m.amount
  }

  // 176 — as close_shift(): issue moves in cash, refund moves in cash (stored negative)
  let prepaidCashIn = 0
  let prepaidCashOut = 0
  for (const m of prepaidMoves) {
    if (m.method !== 'cash') continue
    if (m.kind === 'issue') prepaidCashIn += m.amount
    else if (m.kind === 'refund') prepaidCashOut -= m.amount
  }

  const cashIn = cashSales + creditDownPayments + debtRepayments - cashReturns

  return {
    cashSales,
    creditDownPayments,
    debtRepayments,
    cashReturns,
    supplierCash,
    drawerIn,
    drawerOut,
    changeKeptIn,
    changeKeptOut,
    prepaidCashIn,
    prepaidCashOut,
    cashIn,
    netCash: cashIn - supplierCash + drawerIn - drawerOut + changeKeptIn - changeKeptOut + prepaidCashIn - prepaidCashOut,
  }
}

/**
 * 103 — which column a sale's money belongs in, on every screen that shows
 * sales by how they were paid.
 *
 * ONE rule, shared with `close_shift()`: a ნისია sale counts WHOLE in the
 * credit column (that is what was sold on credit); every other sale counts by
 * the RAIL that took the money, so ₾2 cash + ₾4 card adds 2 to cash and 4 to
 * card. A return is the same with the sign turned. Reading `payment_method`
 * alone puts a split sale in no column at all.
 *
 * It was written out three times — the accounting money section, the reports
 * tab's period totals, its daily journal — and a fourth time as the card line
 * of the shift close (audit, 2026-09-23 → one function 2026-09-25).
 */
export type SaleRails = { cash: number; card: number; credit: number; prepaid: number }

/** 175 — the prepaid rail: the share a gift card or a deposit paid (a return: what went back
 *  to it). The four add up to the signed total of every sale (cash + card + credit + prepaid). */
export function saleRails(s: Pick<Sale, 'type' | 'total' | 'payment_method' | 'paid_cash' | 'paid_card' | 'paid_prepaid'>): SaleRails {
  const sign = s.type === 'return' ? -1 : 1
  if (s.payment_method === 'credit') return { cash: 0, card: 0, credit: sign * s.total, prepaid: 0 }
  return { cash: sign * (s.paid_cash ?? 0), card: sign * (s.paid_card ?? 0), credit: 0, prepaid: s.paid_prepaid ? sign * s.paid_prepaid : 0 }
}

/** The rails of many sales, summed — the one fold every report reads (1.57: with the prepaid). */
export function sumRails(rows: readonly Pick<Sale, 'type' | 'total' | 'payment_method' | 'paid_cash' | 'paid_card' | 'paid_prepaid'>[]): SaleRails {
  const t: SaleRails = { cash: 0, card: 0, credit: 0, prepaid: 0 }
  for (const s of rows) {
    const r = saleRails(s)
    t.cash += r.cash
    t.card += r.card
    t.credit += r.credit
    t.prepaid += r.prepaid
  }
  return t
}

/**
 * A shift's expected drawer. Term for term the same as `close_shift()` (107):
 *
 *   opening + Σ paid_cash over sales + cash debt repayments
 *           − Σ paid_cash over returns − cash supplier payments
 *           + money put in − money taken out
 *           + the change rounding kept − given (168)
 *
 * (`cashSales + creditDownPayments` IS Σ paid_cash over sales — they are split
 * apart only because the Z-report prints them on separate lines.)
 */
export function expectedCash(openingFloat: number, t: CashTerms): number {
  return openingFloat + t.netCash
}

/**
 * 1.41 — one shift's figures as the close dialog shows them, and now the
 * shift menu too (the owner's „ა" design: receipts, cash in the drawer, card).
 * ONE place: the close dialog wrote this out inline, and a second copy in the
 * menu would be the third copy that disagrees (see `cashTerms`). The rows are
 * filtered here by the shift's id; the cash arithmetic is `cashTerms`, the
 * card column `saleRails` (a mixed sale gives its card half).
 */
export type ShiftPreview = {
  cash: number; card: number; credit: number; creditPaid: number
  returns: number; count: number; custCash: number; suppCash: number
  drawerIn: number; drawerOut: number; expected: number
  /** 168 — the change rounding, kept / given (both ≥ 0) */
  keptIn: number; keptOut: number
  /** 176 — money held for customers: taken / handed back by rail, and the sales a prepaid paid */
  prepaidInCash: number; prepaidInCard: number; prepaidOutCash: number; prepaidOutCard: number
  prepaidSales: number
}

export function shiftPreview(
  shift: { id: string; opening_cash: number },
  rows: {
    sales: readonly Sale[]
    customerPayments: readonly CustomerPayment[]
    supplierPayments: readonly SupplierPayment[]
    cashMovements: readonly CashMovement[]
    /** 176 — REQUIRED: a gift card or a deposit taken in cash is in the drawer. It was optional
     *  and both screens that preview a close left it out — the walk on the 1.57 preview showed
     *  „expected ₾20" for a drawer of ₾70; the type now makes every caller say what it has */
    prepaidMoves: readonly PrepaidMove[]
  },
): ShiftPreview {
  const ss = rows.sales.filter(s => s.shift_id === shift.id)
  const pm = rows.prepaidMoves.filter(m => m.shift_id === shift.id)
  const t = cashTerms({
    sales: ss,
    customerPayments: rows.customerPayments.filter(p => p.shift_id === shift.id),
    supplierPayments: rows.supplierPayments.filter(p => p.shift_id === shift.id),
    cashMovements: rows.cashMovements.filter(m => m.shift_id === shift.id),
    prepaidMoves: pm,
  })
  const by = prepaidByMethod(pm)
  const sum = (pred: (s: Sale) => boolean) => ss.filter(pred).reduce((a, s) => a + s.total, 0)
  // 103 — mirrors `close_shift()`: the CARD rail's own takings
  const card = ss.filter(s => s.type === 'sale').reduce((a, s) => a + saleRails(s).card, 0)
  return {
    cash: t.cashSales, card, credit: sum(s => s.payment_method === 'credit' && s.type === 'sale'),
    creditPaid: t.creditDownPayments, returns: sum(s => s.type === 'return'),
    count: ss.filter(s => s.type === 'sale').length,
    custCash: t.debtRepayments, suppCash: t.supplierCash,
    drawerIn: t.drawerIn, drawerOut: t.drawerOut,
    keptIn: t.changeKeptIn, keptOut: t.changeKeptOut,
    prepaidInCash: by.inCash, prepaidInCard: by.inCard, prepaidOutCash: by.outCash, prepaidOutCard: by.outCard,
    // 176 — as close_shift(): Σ paid_prepaid over the shift's SALES, gross like `cash` and
    // `card`; a return's prepaid share is inside `returns`, as a cash or card refund is
    prepaidSales: cents(ss.filter(s => s.type === 'sale').reduce((a, s) => a + (s.paid_prepaid ?? 0), 0)),
    expected: expectedCash(shift.opening_cash, t),
  }
}

/**
 * 1.39 — a shift's returns by where the money went back. SAMGORI2026 called on
 * 2026-10-12: a card sale had been returned by card, and the close looked
 * wrong. It was right — but „დაბრუნებები" was ONE line for every rail, while
 * the expected cash takes off only the cash part (`cashTerms.cashReturns`), so
 * nobody could see why the drawer moved by less than the returns did.
 *
 *   cash      handed back from the drawer — Σ paid_cash
 *   card      refunded on the card's terminal — Σ paid_card
 *   credit    the rest: a ნისია sale's return lowers the debt, no money moves
 *   exchange  1.36's exchanges, a return leg + a sale leg on the same rail:
 *             an item for an item, no refund (Codex, 1.39 review — an equal
 *             swap read as „₾30 from the drawer"). Kept out of the three above.
 *   exchangeCash  the exchange legs' paid_cash — still a term of the expected
 *             cash, which `cash + exchangeCash` reaches exactly (= Σ paid_cash
 *             over returns, what `cashTerms` and `close_shift()` take off).
 */
export type ReturnsSplit = { cash: number; card: number; credit: number; prepaid: number; exchange: number; exchangeCash: number; total: number }

const cents = (v: number) => Math.round(v * 100) / 100

type ReturnRow = Pick<Sale, 'type' | 'total' | 'paid_cash' | 'paid_card' | 'paid_prepaid' | 'exchange_id'>

export function returnsSplit(rows: readonly ReturnRow[]): ReturnsSplit {
  let cash = 0
  let card = 0
  let prepaid = 0   // 175 — back onto the gift card or deposit it came from
  let refunded = 0
  let exchange = 0
  let exchangeCash = 0
  for (const s of rows) {
    if (s.type !== 'return') continue
    if (s.exchange_id) {
      exchange += s.total
      exchangeCash += s.paid_cash ?? 0
      continue
    }
    cash += s.paid_cash ?? 0
    card += s.paid_card ?? 0
    prepaid += s.paid_prepaid ?? 0
    refunded += s.total
  }
  return {
    cash: cents(cash), card: cents(card), prepaid: cents(prepaid), credit: cents(Math.max(0, refunded - cash - card - prepaid)),
    exchange: cents(exchange), exchangeCash: cents(exchangeCash), total: cents(refunded + exchange),
  }
}

/** `returnsSplit` per shift, for the screens that show many shifts' Z-reports. */
export function returnsByShift(
  rows: readonly (ReturnRow & { shift_id?: string | null })[],
): Map<string, ReturnsSplit> {
  const by = new Map<string, ReturnRow[]>()
  for (const s of rows) {
    if (s.type !== 'return' || !s.shift_id) continue
    const list = by.get(s.shift_id) ?? []
    list.push(s)
    by.set(s.shift_id, list)
  }
  return new Map([...by].map(([id, list]) => [id, returnsSplit(list)]))
}

/**
 * The split is shown only when it adds up to the shift's own returns total —
 * the frozen `returns_total` of a closed shift, the open shift's sum. A shift
 * older than the history this device loaded may have returns not in memory;
 * it keeps its one line rather than a split that does not add up.
 */
export function shownSplit(split: ReturnsSplit | null | undefined, returnsTotal: number): ReturnsSplit | null {
  if (!split || returnsTotal < 0.005) return null
  return Math.abs(split.total - returnsTotal) < 0.005 ? split : null
}

/**
 * 1.39 — the expected cash written out term by term under its figure in the
 * close dialog („₾20.00 საწყისი − ₾2.00 დაბრუნება ნაღდით = ₾18.00"), the
 * terms of `expectedCash` in the drawer's order. The opening is always shown;
 * a zero term is left out. Null when the terms do not reach `expected` — a
 * closed shift whose cash refunds are not all in memory keeps the bare figure
 * rather than a sum that does not add up.
 */
export type DrawerTermKey = 'opening' | 'cashSales' | 'creditPaid' | 'debtPayments' | 'cashRefunds' | 'exchangeCash' | 'supplierCash' | 'drawerIn' | 'drawerOut' | 'keptIn' | 'keptOut' | 'prepaidIn' | 'prepaidOut'
export type DrawerTerm = { key: DrawerTermKey; sign: 1 | -1; amount: number }

const DRAWER_SIGNS: [DrawerTermKey, 1 | -1][] = [
  ['opening', 1], ['cashSales', 1], ['creditPaid', 1], ['debtPayments', 1],
  ['cashRefunds', -1], ['exchangeCash', -1], ['supplierCash', -1], ['drawerIn', 1], ['drawerOut', -1],
  ['keptIn', 1], ['keptOut', -1],   // 168
  ['prepaidIn', 1], ['prepaidOut', -1],   // 176
]

export function drawerChain(terms: Record<DrawerTermKey, number>, expected: number): DrawerTerm[] | null {
  const chain = DRAWER_SIGNS
    .filter(([key]) => key === 'opening' || Math.abs(terms[key]) >= 0.005)
    .map(([key, sign]) => ({ key, sign, amount: cents(terms[key]) }))
  const sum = chain.reduce((a, x) => a + x.sign * x.amount, 0)
  return Math.abs(sum - expected) < 0.005 ? chain : null
}

/**
 * What customers still owe, RIGHT NOW — a balance, not a period total, and it
 * says so wherever it is shown.
 *
 * Only positive balances are counted. A customer in credit with us has not
 * lent anything to a customer who owes us, and netting the two would report a
 * debt smaller than the one we would actually go and collect.
 */
export function outstandingDebt(customers: Customer[]): number {
  return customers.reduce((a, c) => a + Math.max(0, c.balance), 0)
}


/**
 * Debt repayments split by how they were taken.
 *
 * `cashTerms` deliberately keeps only the CASH share, because that is the only
 * one that reaches the drawer. But money collected on a debt by card or bank
 * transfer is still money collected, and it was appearing NOWHERE: the three
 * columns of the money section are SALES by method, and the drawer line is
 * cash. A shop with a terminal can settle a ნისია on it today — the credit
 * page has recorded the method and the bank since migration 028 — and the
 * accounting page stayed silent about it.
 *
 * ⚠️ `cardByProvider` exists for one specific job: **reconciling the bank
 * statement.** A card repayment goes through the SAME terminal as a card sale,
 * so the statement shows their sum. Reporting card sales alone leaves the
 * owner a difference with no name on it.
 *
 * It does NOT belong inside the card SALES column — that column answers „how
 * was this sold", and folding a repayment into it would repeat exactly the
 * confusion this module was extracted to end. It belongs beside it, labelled.
 */
export type RepaymentSplit = {
  cash: number
  card: number
  transfer: number
  cardByProvider: { provider: CardProvider; amount: number }[]
  /** Which account a transfer reached — the statement to find it on (2026-09-26). */
  transferByBank: { provider: CardProvider; amount: number }[]
  total: number
}

export function repaymentsByMethod(payments: CustomerPayment[]): RepaymentSplit {
  let cash = 0, card = 0, transfer = 0
  const byProvider = new Map<CardProvider, number>()
  const byBank = new Map<CardProvider, number>()
  for (const p of payments) {
    if (p.method === 'cash') cash += p.amount
    else if (p.method === 'transfer') {
      transfer += p.amount
      if (p.card_provider) byBank.set(p.card_provider, (byBank.get(p.card_provider) ?? 0) + p.amount)
    } else if (p.method === 'card') {
      card += p.amount
      if (p.card_provider) byProvider.set(p.card_provider, (byProvider.get(p.card_provider) ?? 0) + p.amount)
    }
  }
  return {
    cash, card, transfer,
    cardByProvider: [...byProvider.entries()].map(([provider, amount]) => ({ provider, amount })),
    transferByBank: [...byBank.entries()].map(([provider, amount]) => ({ provider, amount })),
    total: cash + card + transfer,
  }
}

/**
 * 176 — money taken for a gift card or a deposit, and handed back, by rail. The cash is a
 * drawer term (`cashTerms`); the card is the terminal's (the bank statement shows it beside
 * the card sales, so it is named per bank, as `repaymentsByMethod` does for ნისია); a bank
 * transfer touches neither and appears only here, per bank. Not takings, never in a sales column.
 */
export type PrepaidSplit = {
  inCash: number; inCard: number; inTransfer: number
  outCash: number; outCard: number; outTransfer: number
  cardByProvider: { provider: CardProvider; amount: number }[]
  transferByBank: { provider: CardProvider; amount: number }[]
}

export function prepaidByMethod(moves: readonly PrepaidMove[]): PrepaidSplit {
  const r = { inCash: 0, inCard: 0, inTransfer: 0, outCash: 0, outCard: 0, outTransfer: 0 }
  const byProvider = new Map<CardProvider, number>()
  const byBank = new Map<CardProvider, number>()
  for (const m of moves) {
    if (m.kind !== 'issue' && m.kind !== 'refund') continue
    const amount = Math.abs(m.amount)
    const into = m.kind === 'issue'
    if (m.method === 'cash') {
      if (into) r.inCash += amount; else r.outCash += amount
    } else if (m.method === 'card') {
      if (into) r.inCard += amount; else r.outCard += amount
      // by bank, as the line above it: what was TAKEN (Codex, final review #5 — a net child under
      // a gross parent read „ბარათი ₾50 (TBC ₾40)"); a refund is its own figure
      if (m.card_provider && into) byProvider.set(m.card_provider, (byProvider.get(m.card_provider) ?? 0) + amount)
    } else if (m.method === 'transfer') {
      if (into) r.inTransfer += amount; else r.outTransfer += amount
      if (m.card_provider && into) byBank.set(m.card_provider, (byBank.get(m.card_provider) ?? 0) + amount)
    }
  }
  return {
    inCash: cents(r.inCash), inCard: cents(r.inCard), inTransfer: cents(r.inTransfer),
    outCash: cents(r.outCash), outCard: cents(r.outCard), outTransfer: cents(r.outTransfer),
    cardByProvider: [...byProvider.entries()].map(([provider, amount]) => ({ provider, amount: cents(amount) })),
    transferByBank: [...byBank.entries()].map(([provider, amount]) => ({ provider, amount: cents(amount) })),
  }
}
